import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, Pencil, Search, Package, Wrench, X, Save, Lock, Tag, Building2 } from "lucide-react";

const euro = (n) => (n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const UNITS = ["u", "m", "m²", "ml", "h", "forfait", "lot", "kg"];
const emptyForm = () => ({ reference: "", name: "", kind: "produit", category: "", unit: "u", cost_price: 0, unit_price: 0, vat_rate: 20, description: "", scope: "private" });

const margin = (it) => (it.unit_price || 0) - (it.cost_price || 0);
const marginPct = (it) => (it.unit_price ? (margin(it) / it.unit_price) * 100 : 0);

export default function Catalog() {
  const { user } = useAuth();
  const isFabrikoAdmin = user?.role === "fabriko_admin";
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [detail, setDetail] = useState(null);

  const load = () => {
    api.get("/catalog").then((r) => setItems(r.data)).catch(() => {});
    api.get("/catalog/categories").then((r) => setCategories(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const canEdit = (it) => (it.is_global ? isFabrikoAdmin : true);

  const openNew = () => { setForm({ ...emptyForm(), scope: isFabrikoAdmin ? "global" : "private" }); setEditId(null); setShowForm(true); };
  const openEdit = (it) => {
    setForm({ reference: it.reference || "", name: it.name, kind: it.kind, category: it.category || "", unit: it.unit,
      cost_price: it.cost_price, unit_price: it.unit_price, vat_rate: it.vat_rate, description: it.description || "",
      scope: it.is_global ? "global" : "private" });
    setEditId(it.id); setDetail(null); setShowForm(true);
  };
  const save = async () => {
    if (!form.name.trim()) return toast.error("La désignation est obligatoire");
    const payload = { ...form, cost_price: +form.cost_price || 0, unit_price: +form.unit_price || 0, vat_rate: +form.vat_rate || 0 };
    try {
      if (editId) { await api.put(`/catalog/${editId}`, payload); toast.success("Article mis à jour"); }
      else { await api.post("/catalog", payload); toast.success("Article ajouté"); }
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement"); }
  };
  const remove = async (it) => {
    if (!window.confirm("Supprimer cet article du catalogue ?")) return;
    try { await api.delete(`/catalog/${it.id}`); load(); setDetail(null); toast.success("Article supprimé"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const filtered = items.filter((it) =>
    (activeCat === "all" || (it.category || "") === activeCat) &&
    (!query || it.name.toLowerCase().includes(query.toLowerCase()) || (it.reference || "").toLowerCase().includes(query.toLowerCase())));

  const set = (k, v) => setForm({ ...form, [k]: v });
  const pill = (on) => `px-3 py-1.5 rounded-full text-sm font-medium transition-all ${on ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`;

  return (
    <div className="space-y-5 animate-fade-up" data-testid="catalog-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold text-navy">Catalogue Produits & Services</h1>
          <p className="text-sm text-slate-500 mt-0.5">Catalogue Fabriko partagé + vos articles privés. Cliquez un titre pour ouvrir la fiche.</p>
        </div>
        <button onClick={openNew} data-testid="catalog-add-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy-deep transition-all active:scale-95">
          <Plus className="w-4 h-4" /> {isFabrikoAdmin ? "Nouvel article Fabriko" : "Nouvel article"}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="catalog-search"
            placeholder="Rechercher un article ou une référence…"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-400" />
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="catalog-categories">
        <button onClick={() => setActiveCat("all")} data-testid="cat-filter-all" className={pill(activeCat === "all")}>Tout</button>
        {categories.map((c) => <button key={c} onClick={() => setActiveCat(c)} data-testid={`cat-filter-${c}`} className={pill(activeCat === c)}><Tag className="w-3 h-3 inline mr-1" />{c}</button>)}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <th className="text-left px-4 py-3 font-medium">Article</th>
                <th className="text-left px-3 py-3 font-medium">Catégorie</th>
                <th className="text-left px-3 py-3 font-medium">Type</th>
                <th className="text-right px-3 py-3 font-medium">Coût d'achat</th>
                <th className="text-right px-3 py-3 font-medium">Prix vente HT</th>
                <th className="text-right px-3 py-3 font-medium">Marge</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} className="border-t border-slate-100 hover:bg-sky-50/40" data-testid={`catalog-row-${it.id}`}>
                  <td className="px-4 py-3">
                    <button onClick={() => setDetail(it)} data-testid={`catalog-title-${it.id}`} className="text-left font-semibold text-navy hover:text-sky-600 hover:underline">
                      {it.name}
                    </button>
                    <div className="flex items-center gap-2 mt-0.5">
                      {it.reference && <span className="text-xs text-slate-400 font-mono">{it.reference}</span>}
                      {it.is_global && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded-full"><Building2 className="w-2.5 h-2.5" /> Fabriko</span>}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-slate-500">{it.category || "—"}</td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${it.kind === "service" ? "bg-violet-50 text-violet-600" : "bg-sky-50 text-sky-600"}`}>
                      {it.kind === "service" ? <Wrench className="w-3 h-3" /> : <Package className="w-3 h-3" />}
                      {it.kind === "service" ? "Service" : "Produit"}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-slate-600">{euro(it.cost_price)}</td>
                  <td className="px-3 py-3 text-right font-mono text-navy font-medium">{euro(it.unit_price)}</td>
                  <td className="px-3 py-3 text-right font-mono">
                    <span className={margin(it) >= 0 ? "text-emerald-600" : "text-rose-500"}>
                      {euro(margin(it))} <span className="text-xs opacity-70">({marginPct(it).toFixed(0)}%)</span>
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    {canEdit(it) ? (
                      <>
                        <button onClick={() => openEdit(it)} data-testid={`catalog-edit-${it.id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-sky-600 hover:bg-sky-50"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => remove(it)} data-testid={`catalog-delete-${it.id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
                      </>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-slate-400" title="Article Fabriko en lecture seule"><Lock className="w-3.5 h-3.5" /></span>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">
                  {items.length === 0 ? "Votre catalogue est vide. Ajoutez votre premier article." : "Aucun article ne correspond à votre recherche."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fiche produit (drawer) */}
      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={() => setDetail(null)} data-testid="catalog-detail-drawer">
          <div className="bg-white w-full max-w-md h-full overflow-y-auto p-6 shadow-xl animate-slide-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl font-display font-bold text-navy">{detail.name}</h2>
                  {detail.is_global && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-sky-700 bg-sky-100 px-2 py-0.5 rounded-full"><Building2 className="w-3 h-3" /> Catalogue Fabriko</span>}
                </div>
                {detail.reference && <div className="text-xs text-slate-400 font-mono mt-1">Réf. {detail.reference}</div>}
              </div>
              <button onClick={() => setDetail(null)} data-testid="catalog-detail-close" className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${detail.kind === "service" ? "bg-violet-50 text-violet-600" : "bg-sky-50 text-sky-600"}`}>
                {detail.kind === "service" ? <Wrench className="w-3 h-3" /> : <Package className="w-3 h-3" />} {detail.kind === "service" ? "Service" : "Produit"}
              </span>
              {detail.category && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-slate-100 text-slate-600"><Tag className="w-3 h-3" /> {detail.category}</span>}
            </div>
            {detail.description && <p className="mt-4 text-sm text-slate-600 leading-relaxed">{detail.description}</p>}
            <div className="mt-5 rounded-xl border border-slate-200 divide-y divide-slate-100">
              {[["Unité", detail.unit], ["Coût d'achat HT", euro(detail.cost_price)], ["Prix de vente HT", euro(detail.unit_price)],
                ["Marge unitaire", `${euro(margin(detail))} (${marginPct(detail).toFixed(0)}%)`], ["TVA", `${detail.vat_rate}%`]].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="text-slate-500">{k}</span><span className="font-mono font-medium text-navy">{v}</span>
                </div>
              ))}
            </div>
            {canEdit(detail) && (
              <button onClick={() => openEdit(detail)} data-testid="catalog-detail-edit" className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy-deep">
                <Pencil className="w-4 h-4" /> Modifier l'article
              </button>
            )}
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowForm(false)} data-testid="catalog-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-display font-bold text-navy">{editId ? "Modifier l'article" : "Nouvel article"}</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {isFabrikoAdmin && (
                <div className="col-span-2">
                  <label className="text-xs font-medium text-slate-500">Portée</label>
                  <select value={form.scope} onChange={(e) => set("scope", e.target.value)} data-testid="catalog-form-scope"
                    className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-400">
                    <option value="global">Catalogue Fabriko (proposé à tous les ateliers)</option>
                    <option value="private">Privé (mon espace)</option>
                  </select>
                </div>
              )}
              <div className="col-span-2 sm:col-span-1">
                <label className="text-xs font-medium text-slate-500">Référence</label>
                <input value={form.reference} onChange={(e) => set("reference", e.target.value)} data-testid="catalog-form-reference"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className="text-xs font-medium text-slate-500">Type</label>
                <select value={form.kind} onChange={(e) => set("kind", e.target.value)} data-testid="catalog-form-kind"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-400">
                  <option value="produit">Produit</option>
                  <option value="service">Service</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-500">Désignation *</label>
                <input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="catalog-form-name"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-500">Catégorie <span className="text-slate-400">(tapez pour en créer une nouvelle)</span></label>
                <input list="cat-list" value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="catalog-form-category"
                  placeholder="Matières, Main d'œuvre, Accessoires…"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
                <datalist id="cat-list">{categories.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Unité</label>
                <select value={form.unit} onChange={(e) => set("unit", e.target.value)} data-testid="catalog-form-unit"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-400">
                  {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">TVA</label>
                <select value={form.vat_rate} onChange={(e) => set("vat_rate", +e.target.value)} data-testid="catalog-form-vat"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-400">
                  {[0, 5.5, 10, 20].map((r) => <option key={r} value={r}>{r}%</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Coût d'achat HT</label>
                <input type="number" value={form.cost_price} onChange={(e) => set("cost_price", e.target.value)} data-testid="catalog-form-cost"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-right font-mono focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Prix de vente HT</label>
                <input type="number" value={form.unit_price} onChange={(e) => set("unit_price", e.target.value)} data-testid="catalog-form-price"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-right font-mono focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
              <div className="col-span-2 rounded-xl bg-slate-50 px-3 py-2.5 flex justify-between text-sm">
                <span className="text-slate-500">Marge unitaire</span>
                <span className={`font-mono font-semibold ${margin(form) >= 0 ? "text-emerald-600" : "text-rose-500"}`} data-testid="catalog-form-margin">
                  {euro(margin(form))} ({marginPct(form).toFixed(0)}%)
                </span>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-500">Description (optionnel)</label>
                <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} data-testid="catalog-form-description"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium hover:bg-slate-50">Annuler</button>
              <button onClick={save} data-testid="catalog-form-save" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy-deep">
                <Save className="w-4 h-4" /> Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
